import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import Pagination from "@/components/base/Pagination";
import { downloadCsv, toCsv } from "@/lib/csv";
import { parseCustomersCsv, executeImport } from "@/lib/imports";
import StagedImportModal from "@/components/feature/StagedImportModal";
import AddressFields, { emptyAddress, addressToJson, type AddressValues } from "@/components/feature/AddressFields";
import { useCreateDeepLink } from "@/hooks/useCreateDeepLink";
import { canManageCustomers } from "@/lib/permissions";

interface CustomerRow {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: string;
}

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

/**
 * Escape LIKE wildcards so a literal `%`/`_`/`\` in a query is matched, then
 * strip the characters that are reserved by PostgREST's `.or()` filter syntax.
 * Prevents wildcard injection without silently dropping legitimate punctuation.
 */
const sanitize = (q: string) => q.replace(/[\\%_]/g, "\\$&").replace(/[(),]/g, "").trim();

interface FormState {
  name: string;
  email: string;
  phone: string;
  type: string;
  customer_reference: string;
  notes: string;
  billing: AddressValues;
  shipping: AddressValues;
}

const emptyForm = (): FormState => ({
  name: "",
  email: "",
  phone: "",
  type: "individual",
  customer_reference: "",
  notes: "",
  billing: emptyAddress(),
  shipping: emptyAddress(),
});

export default function Customers() {
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const canManage = canManageCustomers(role);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<FormState>(emptyForm());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [importOpen, setImportOpen] = useState(false);

  useCreateDeepLink(() => setOpen(true));

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["customers", org?.id, search, statusFilter, page, pageSize],
    enabled: !!org?.id,
    queryFn: async () => {
      let q = supabase
        .from("customers")
        .select("id, name, email, phone, status", { count: "exact" })
        .eq("org_id", org!.id);
      const term = sanitize(search);
      if (term) {
        const ilike = `%${term}%`;
        q = q.or(`name.ilike.${ilike},email.ilike.${ilike},phone.ilike.${ilike}`);
      }
      if (statusFilter) q = q.eq("status", statusFilter);
      q = q.order("name").range(page * pageSize, page * pageSize + pageSize - 1);
      const { data, count } = await q;
      return { rows: (data ?? []) as CustomerRow[], total: count ?? 0 };
    },
  });

  const set =
    (k: keyof Pick<FormState, "name" | "email" | "phone" | "customer_reference" | "notes">) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const { error: insErr } = await supabase.from("customers").insert({
      org_id: org!.id,
      name: form.name,
      email: form.email || null,
      phone: form.phone || null,
      type: form.type || "individual",
      customer_reference: form.customer_reference || null,
      notes: form.notes || null,
      billing_address: addressToJson(form.billing),
      shipping_address: addressToJson(form.shipping),
    });
    setSaving(false);
    if (insErr) {
      setError(insErr.code === "23505" ? "A customer with this email already exists." : insErr.message);
      return;
    }
    setOpen(false);
    setForm(emptyForm());

    queryClient.invalidateQueries({ queryKey: ["customers", org?.id] });

    // Sales Order creation embeds the customer directory in its cached query
    // result. Ensure a newly created customer is immediately selectable.
    queryClient.invalidateQueries({ queryKey: ["sales-orders", org?.id] });
  };

  const handleExport = async () => {
    const { data } = await supabase
      .from("customers")
      .select("id, name, email, phone, status")
      .eq("org_id", org!.id)
      .order("name");
    const rows = (data ?? []) as CustomerRow[];
    const headers = ["name", "email", "phone", "status"];
    const csv = rows.map((c) => [c.name, c.email ?? "", c.phone ?? "", c.status]);
    downloadCsv(`customers-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, csv));
  };

  return (
    <div>
      <PageHeader title="Customers" subtitle="Manage customer directory">
        <button onClick={handleExport} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-download-2-line" /> Export
        </button>
        {canManage && (
          <button onClick={() => setImportOpen(true)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-upload-2-line" /> Import
          </button>
        )}
        {canManage && (
          <button onClick={() => setOpen(true)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line" />
            Add Customer
          </button>
        )}
      </PageHeader>

      {isLoading ? (
        <ListSkeleton />
      ) : isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load customers.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      ) : !data || data.total === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState
            icon="ri-user-3-line"
            title="No customers yet"
            description="Customers are who you sell to. Add your first one so you can start creating sales orders."
            action={
              canManage ? (
                <button onClick={() => setOpen(true)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                  <i className="ri-add-line" /> Add your first customer
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search by name, email or phone..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="">All statuses</option>
              {["active", "inactive"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows ?? []).map((c) => (
                  <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-semibold">
                          {c.name.charAt(0).toUpperCase()}
                        </div>
                        <Link to={`/customers/${c.id}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700 hover:underline">{c.name}</Link>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{c.email ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{c.phone ?? "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={data.total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} />
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add Customer"
        footer={
          <>
            <button onClick={() => setOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : "Save Customer"}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div>
            <label className={labelCls}>Name *</label>
            <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Acme Corp" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="orders@acme.com" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+61 2 0000 0000" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Type</label>
              <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className={inputCls}>
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Reference</label>
              <input value={form.customer_reference} onChange={set("customer_reference")} className={inputCls} placeholder="Internal code" />
            </div>
          </div>
          <AddressFields label="Billing address" value={form.billing} onChange={(v) => setForm((f) => ({ ...f, billing: v }))} />
          <AddressFields label="Shipping address" value={form.shipping} onChange={(v) => setForm((f) => ({ ...f, shipping: v }))} />
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} rows={3} maxLength={500} />
          </div>
        </form>
      </Modal>

      <StagedImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import customers"
        description="Import customers from a CSV file. Rows are matched to existing customers by email; name-only matches require manual review."
        requiredColumns={["name"]}
        optionalColumns={["email", "phone"]}
        templateHeaders={["name", "email", "phone"]}
        templateRows={[["Acme Corp", "orders@acme.com", "+61 2 0000 0000"]]}
        onParse={(rows, headers) => parseCustomersCsv(rows, headers, org!.id)}
        onExecute={(rows) => executeImport("customers", org!.id, rows)}
        onDone={() => queryClient.invalidateQueries({ queryKey: ["customers", org?.id] })}
      />
    </div>
  );
}